<template>
  <slot
    :filters-open="filtersOpen"
    :toggle-filters="toggleFilters"
    :active-filters="activeFilters"
    :toggle-filter="toggleFilter"
    :is-filter-active="isFilterActive"
    :is-filter-available="isFilterAvailable"
    :clear-filters="clearFilters"
    :filtered-count="filteredCount"
  />
</template>

<script>
export default {
  name: "CollectionFilter",
};
</script>

<script setup>
import {ref, reactive, onMounted} from "vue";

const props = defineProps({
  totalCount: {
    type: Number,
    default: 0,
  },
  sizeValues: {
    type: String,
    default: "",
  },
  needValues: {
    type: String,
    default: "",
  },
  currentUrl: {
    type: String,
    default: "",
  },
});

const filtersOpen = ref(false);
const activeFilters = reactive({});
const filteredCount = ref(props.totalCount);
const availableFilters = reactive({});

const sizeValues = props.sizeValues
  ? props.sizeValues.split(",").map(s => s.trim()).filter(Boolean)
  : [];
const needValues = props.needValues
  ? props.needValues.split(",").map(s => s.trim()).filter(Boolean)
  : [];

const toggleFilters = () => {
  filtersOpen.value = !filtersOpen.value;
};

const isFilterActive = (group, value) => {
  if (!activeFilters[group]) return false;
  return activeFilters[group].includes(value);
};

const isFilterAvailable = (group, value) => {
  const key = `${group}:${value}`;
  return availableFilters[key] !== false;
};

const toggleFilter = (group, value) => {
  if (!activeFilters[group]) {
    activeFilters[group] = [];
  }

  const index = activeFilters[group].indexOf(value);
  if (index === -1) {
    activeFilters[group].push(value);
  } else {
    activeFilters[group].splice(index, 1);
  }

  applyClientFilters();
};

const clearFilters = () => {
  Object.keys(activeFilters).forEach(key => {
    delete activeFilters[key];
  });
  applyClientFilters();
};

// Get all card elements and their tags
const getCards = () => {
  return Array.from(document.querySelectorAll(".plp-card[data-tags]")).map(
    card => ({
      el: card,
      tags: (card.getAttribute("data-tags") || "")
        .split(",")
        .map(t => t.trim().toLowerCase()),
    }),
  );
};

// Check if a card matches a specific filter group
const cardMatchesGroup = (tags, group, values) => {
  if (!values || values.length === 0) return true;
  return values.some(v => tags.includes(v.toLowerCase()));
};

const applyClientFilters = () => {
  const cards = getCards();
  const activeSizes = activeFilters.size || [];
  const activeNeeds = activeFilters.need || [];
  let visibleCount = 0;

  // Show/hide cards
  cards.forEach(({el, tags}) => {
    const matchesSize = cardMatchesGroup(tags, "size", activeSizes);
    const matchesNeed = cardMatchesGroup(tags, "need", activeNeeds);
    const show = matchesSize && matchesNeed;

    el.style.display = show ? "" : "none";
    if (show) visibleCount++;
  });

  filteredCount.value =
    activeSizes.length === 0 && activeNeeds.length === 0
      ? props.totalCount
      : visibleCount;

  // Update available filters based on current selection
  updateAvailableFilters(cards, activeSizes, activeNeeds);
};

const updateAvailableFilters = (cards, activeSizes, activeNeeds) => {
  // For each size value, check if selecting it (combined with current need filters) would show any products
  sizeValues.forEach(size => {
    const wouldMatch = cards.some(({tags}) => {
      const matchesSize = tags.includes(size.toLowerCase());
      const matchesNeed = cardMatchesGroup(tags, "need", activeNeeds);
      return matchesSize && matchesNeed;
    });
    availableFilters[`size:${size}`] = wouldMatch;
  });

  // For each need value, check if selecting it (combined with current size filters) would show any products
  needValues.forEach(need => {
    const wouldMatch = cards.some(({tags}) => {
      const matchesNeed = tags.includes(need.toLowerCase());
      const matchesSize = cardMatchesGroup(tags, "size", activeSizes);
      return matchesNeed && matchesSize;
    });
    availableFilters[`need:${need}`] = wouldMatch;
  });
};

onMounted(() => {
  // Initialize available filters
  const cards = getCards();
  updateAvailableFilters(cards, [], []);
});
</script>
